import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { zodResolver } from "@hookform/resolvers/zod";
import { Facility } from "types/facility.type";
import { patchFacilitySchema, TPatchFacility } from "./patchFacilitySchema";
import { FacilityQueryKey, patchFacility } from "api/facility.api";
import { useNotification } from "hooks/useNotification";

export const usePatchFacilityLogic = (facility: Facility) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const form = useForm<TPatchFacility>({
    resolver: zodResolver(patchFacilitySchema),
    defaultValues: {
      name: facility.name,
      address: facility.address,
      postalCode: facility.postalCode,
      city: facility.city,
      kommunKod: facility.kommunKod,
      cfarNr: facility.cfarNr,
      contactName: facility.contactName,
      contactEmail: facility.contactEmail,
      contactPhone: facility.contactPhone,
      notes: facility.notes,
      documentationEmail: facility.documentationEmail,
      coordinates: facility.coordinates ?? undefined,
    },
  });

  const resetForm = useCallback(
    (facility: Facility) => {
      form.setValue("name", facility.name);
      form.setValue("address", facility.address);
      form.setValue("postalCode", facility.postalCode);
      form.setValue("city", facility.city);
      form.setValue("kommunKod", facility.kommunKod);
      form.setValue("cfarNr", facility.cfarNr);
      form.setValue("contactName", facility.contactName);
      form.setValue("contactEmail", facility.contactEmail);
      form.setValue("contactPhone", facility.contactPhone);
      form.setValue("notes", facility.notes);
      form.setValue("documentationEmail", facility.documentationEmail);
      form.setValue("coordinates", facility.coordinates ?? undefined);
    },
    [form],
  );

  useEffect(() => {
    if (facility) {
      resetForm(facility);
    }
  }, [facility, form, resetForm]);

  const onModalClose = () => setIsModalOpen(false);

  const afterModalClose = () => resetForm(facility);

  const mutation = useMutation({
    ...patchFacility(facility.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [FacilityQueryKey.GetOne, facility.id],
      });
      onModalClose();
    },
    onError: () => {
      notification.error();
    },
  });

  const onSubmit = form.handleSubmit((data: TPatchFacility) => {
    mutation.mutate(data);
  });

  const copyCustomerAddress = () => {
    const customer = facility.customer;
    if (customer?.address) {
      form.setValue("address", customer.address);
    }
    if (customer?.postalCode) {
      form.setValue("postalCode", customer.postalCode);
    }
    if (customer?.city) {
      form.setValue("city", customer.city);
    }
  };

  const copyCustomerContact = () => {
    const customer = facility.customer;
    if (customer?.contactName) {
      form.setValue("contactName", customer.contactName);
    }
    if (customer?.contactEmail) {
      form.setValue("contactEmail", customer.contactEmail);
    }
    if (customer?.contactPhone) {
      form.setValue("contactPhone", customer.contactPhone);
    }
  };

  return {
    isModalOpen,
    openModal: () => setIsModalOpen(true),
    form,
    onSubmit,
    mutation,
    onModalClose,
    afterModalClose,
    facility,
    copyCustomerAddress,
    copyCustomerContact,
  };
};

export type PatchFacilityLogicRetVal = ReturnType<typeof usePatchFacilityLogic>;
